import { useState } from 'react';
import { Alert, Button } from '@mui/material';
import { Download as DownloadIcon } from '@mui/icons-material';
import { harnessService } from '@/services/api/harnessService';
import { downloadFile } from '@/lib/impedance';
import logger from '@/lib/logger';

export interface ExportButtonProps {
  /** The Batch to export as a downloadable JSON file (Story 3.2). */
  batchId: string;
}

/**
 * Exports a Batch as JSON (Story 3.2, AC1). Fetches the full-fidelity snapshot via
 * `harnessService.exportBatch` (through `apiClient`, so the auth interceptor attaches the
 * token) and hands it to the shared `downloadFile` helper — the same Blob-URL-anchor mechanism
 * `MeasurementAnalysisPage` already uses for its client-side exports. Surfaced both in the
 * active-Batch view and the read-only review view (Story 3.1).
 */
export function ExportButton({ batchId }: ExportButtonProps) {
  const [isExporting, setIsExporting] = useState(false);
  const [hasError, setHasError] = useState(false);

  const handleExport = async () => {
    setIsExporting(true);
    setHasError(false);
    try {
      const { blob, filename } = await harnessService.exportBatch(batchId);
      const text = await blob.text();
      downloadFile(text, filename, 'application/json');
    } catch (err) {
      logger.error(`Failed to export Batch ${batchId}:`, err);
      setHasError(true);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <>
      <Button
        size="small"
        variant="outlined"
        startIcon={<DownloadIcon />}
        onClick={handleExport}
        disabled={isExporting}
      >
        Export as JSON
      </Button>
      {hasError && (
        <Alert severity="error" sx={{ mt: 1 }}>
          Failed to export this Batch. Please try again.
        </Alert>
      )}
    </>
  );
}
